import type { FC } from "react";
import { useMemo } from "react";

import { SWReactIcons } from "@similarweb/icons";

import { getIntentSignalCountries } from "app/pages/sales-intelligence/modules/company-analysis/services/signals/getters/getIntentSignalCountries";
import type { IntentSignalEventInfoDto } from "app/pages/sales-intelligence/modules/company-analysis/types/signals";
import IntentSignalsEventsTooltipContainer from "app/pages/sales-intelligence/pages/company-analysis/components/signals/SignalEventInfo/IntentSignalEventInfo/IntentSignalsEventsTooltip/IntentSignalsEventsTooltipContainer";
import {
    StyledDirectionDeltaValue,
    StyledDirectionDeltaWrapper,
} from "app/pages/sales-intelligence/sub-modules/signals/components/shared/styles";
import SignalsTooltip from "app/pages/sales-intelligence/sub-modules/signals/components/tooltips/SignalsTooltip/SignalsTooltip";
import { TOOLTIP_DEBOUNCE_TIME } from "app/pages/sales-intelligence/sub-modules/signals/constants";
import { DeltaDirections } from "app/pages/sales-intelligence/sub-modules/signals/types/enums";

import IntentSignalsEventsTooltipContent from "./IntentSignalsEventsTooltip/IntentSignalsEventsTooltipContent";
import {
    StyledDirectionIcon,
    StyledIntentTopicWithEventInfoTitle,
    StyledIntentTopicWithEventInfoWrapper,
    StyledScoreValueWrapper,
} from "./styles";

type IntentTopicWithEventInfoProps = {
    eventInfo: IntentSignalEventInfoDto;
};

const IntentTopicWithEventInfo: FC<IntentTopicWithEventInfoProps> = ({ eventInfo }) => {
    const getDeltaDirection = () => {
        if (eventInfo.compositeScoreDelta === 0) {
            return DeltaDirections.INITIAL;
        }

        return eventInfo.compositeScoreDelta < 0 ? DeltaDirections.DOWN : DeltaDirections.UP;
    };

    const deltaDirection = getDeltaDirection();

    const getArrowIcon = () => {
        if (deltaDirection === DeltaDirections.UP) {
            return (
                <SWReactIcons
                    iconName="arrow-up"
                    size="xs"
                />
            );
        }

        if (deltaDirection === DeltaDirections.DOWN) {
            return (
                <SWReactIcons
                    iconName="arrow-down"
                    size="xs"
                />
            );
        }

        return null;
    };

    const mappedCountryAndCities = useMemo(
        () => getIntentSignalCountries(eventInfo.countriesScopes),
        [eventInfo.countriesScopes],
    );

    return (
        <SignalsTooltip
            debounce={TOOLTIP_DEBOUNCE_TIME}
            placement="top"
            content={
                <IntentSignalsEventsTooltipContainer>
                    <IntentSignalsEventsTooltipContent countries={mappedCountryAndCities} />
                </IntentSignalsEventsTooltipContainer>
            }
        >
            <StyledIntentTopicWithEventInfoWrapper>
                <StyledIntentTopicWithEventInfoTitle>{eventInfo.topic}</StyledIntentTopicWithEventInfoTitle>
                <StyledScoreValueWrapper>{eventInfo.compositeScore}</StyledScoreValueWrapper>
                <StyledDirectionDeltaWrapper>
                    {deltaDirection === DeltaDirections.INITIAL ? null : (
                        <StyledDirectionIcon>{getArrowIcon()}</StyledDirectionIcon>
                    )}
                    <StyledDirectionDeltaValue direction={deltaDirection}>
                        {Math.abs(eventInfo.compositeScoreDelta)}
                    </StyledDirectionDeltaValue>
                </StyledDirectionDeltaWrapper>
            </StyledIntentTopicWithEventInfoWrapper>
        </SignalsTooltip>
    );
};

export default IntentTopicWithEventInfo;
